import { FiAward } from 'react-icons/fi';

export const formalRegisterData = {
  courseId: 'formal-register',
  courseType: 'vocabulary',
  title: 'Formal Register',
  icon: FiAward,
  overview: {
    impact: 'Replace informal language with academic alternatives for higher band scores',
    focus: 'Formal vocabulary, sentence structures, and avoiding informality',
    uses: 'Writing Task 2, Writing Task 1, formal Speaking Part 3',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Understanding Register',
      content: `<p><strong>Register</strong> refers to the level of formality in your language. IELTS Writing requires a <strong>formal to semi-formal register</strong> — this means avoiding slang, contractions, colloquial expressions, and overly casual word choices.</p>
<p>The IELTS Writing band descriptors penalise "inappropriate register" or "too informal a tone." Even candidates with strong grammar and vocabulary can lose marks by writing sentences like <em>"Lots of people think that…"</em> instead of <em>"A significant number of people believe that…"</em></p>
<h4>Key Differences</h4>
<ul>
  <li><strong>Informal:</strong> contractions (don't, can't), phrasal verbs (find out, come up with), casual words (kids, stuff, lots of)</li>
  <li><strong>Formal:</strong> full forms (do not, cannot), single-word verbs (discover, devise), precise nouns (children, materials, numerous)</li>
</ul>`,
      points: [
        'IELTS Writing requires formal or semi-formal register — never casual',
        'Informality is penalised even if your grammar is perfect',
        'The three biggest register mistakes: contractions, phrasal verbs, and casual words',
        'Task 1 (Academic) and Task 2 both require formal register; General Training letters vary',
      ],
    },
    {
      title: 'Informal to Formal Vocabulary',
      content: `<p>The following table shows common informal words and their formal equivalents. Memorising these swaps is one of the fastest ways to improve your Writing band score.</p>
<table>
  <thead><tr><th>Informal</th><th>Formal</th><th>Example</th></tr></thead>
  <tbody>
    <tr><td>get</td><td><strong>obtain / acquire</strong></td><td>Students can obtain a degree through online programmes.</td></tr>
    <tr><td>help</td><td><strong>assist / facilitate</strong></td><td>Governments should assist citizens in accessing healthcare.</td></tr>
    <tr><td>think about</td><td><strong>consider / contemplate</strong></td><td>Policymakers should consider the long-term implications.</td></tr>
    <tr><td>a lot of / lots of</td><td><strong>numerous / substantial / considerable</strong></td><td>Numerous studies have confirmed this finding.</td></tr>
    <tr><td>good</td><td><strong>beneficial / advantageous</strong></td><td>Exercise is beneficial for both physical and mental health.</td></tr>
    <tr><td>bad</td><td><strong>detrimental / adverse / unfavourable</strong></td><td>Pollution has adverse effects on respiratory health.</td></tr>
    <tr><td>big</td><td><strong>significant / substantial / considerable</strong></td><td>There has been a significant increase in online learning.</td></tr>
    <tr><td>show</td><td><strong>demonstrate / illustrate / indicate</strong></td><td>The data demonstrates a clear upward trend.</td></tr>
    <tr><td>find out</td><td><strong>discover / ascertain / determine</strong></td><td>Researchers discovered a link between sleep and memory.</td></tr>
    <tr><td>go up / go down</td><td><strong>increase / decrease / rise / decline</strong></td><td>Enrolment figures declined steadily over the period.</td></tr>
    <tr><td>deal with</td><td><strong>address / tackle / manage</strong></td><td>The government must address the issue of youth unemployment.</td></tr>
    <tr><td>kids</td><td><strong>children / young people / adolescents</strong></td><td>Children in rural areas often lack access to quality education.</td></tr>
  </tbody>
</table>`,
      points: [
        'Swap casual words for formal ones — this is the simplest upgrade for your score',
        '"Get" is the most overused informal word in IELTS — replace with obtain, acquire, receive, gain',
        '"A lot of" and "lots of" should always become "numerous", "considerable", or "a significant number of"',
        'Learn 2-3 formal options for each informal word so you can vary your vocabulary',
      ],
    },
    {
      title: 'Avoiding Informality in Writing',
      content: `<p>Beyond word choice, several structural habits mark writing as informal. Eliminating these patterns immediately makes your essays sound more academic.</p>
<h4>1. Contractions</h4>
<p>❌ <em>"It doesn't seem fair that…"</em> → ✅ <em>"It does not seem fair that…"</em></p>
<h4>2. Phrasal Verbs</h4>
<p>❌ <em>"Scientists have found out that…"</em> → ✅ <em>"Scientists have discovered that…"</em></p>
<p>❌ <em>"The government should come up with solutions…"</em> → ✅ <em>"The government should devise solutions…"</em></p>
<h4>3. Personal Pronouns (overuse)</h4>
<p>❌ <em>"I think kids should learn more stuff in school."</em> → ✅ <em>"It is arguable that the school curriculum should encompass a broader range of subjects."</em></p>
<h4>4. Rhetorical Questions</h4>
<p>❌ <em>"But is this really fair?"</em> → ✅ <em>"The fairness of this approach is questionable."</em></p>`,
      points: [
        'Never use contractions in IELTS Writing — always write full forms',
        'Replace phrasal verbs with single-word equivalents: find out → discover, look into → investigate',
        'Limit "I think" to once per essay — use "It is arguable that…" or "One could argue that…"',
        'Convert rhetorical questions into declarative statements',
      ],
    },
    {
      title: 'Formal Sentence Structures',
      content: `<p>Formal register is not just about word choice — it also involves <strong>sentence structure</strong>. Academic writing favours certain patterns that signal formality.</p>
<h4>Impersonal Constructions</h4>
<p><em>"It is widely acknowledged that education is the foundation of economic growth."</em></p>
<p><em>"There is a growing consensus that renewable energy is essential."</em></p>
<h4>Passive Voice (selective use)</h4>
<p><em>"Significant investment has been made in infrastructure."</em> (more formal than "They have invested a lot in infrastructure.")</p>
<h4>Nominal Structures (noun phrases instead of verb phrases)</h4>
<p>❌ <em>"Because the population grew rapidly…"</em> → ✅ <em>"Due to rapid population growth…"</em></p>
<p>❌ <em>"After the government decided to invest…"</em> → ✅ <em>"Following the government's decision to invest…"</em></p>`,
      points: [
        'Start sentences with "It is + adjective + that" for a formal, impersonal tone',
        'Use passive voice selectively — it is formal but overuse makes writing unclear',
        'Convert verb phrases to noun phrases: "decided" → "the decision", "grew" → "growth"',
        'Formal sentence openings: "It is evident that…", "There is considerable debate about…"',
      ],
    },
  ],
  exercises: [
    {
      title: 'Choose the Formal Alternative',
      type: 'select',
      instructions: 'Select the most formal and appropriate word or phrase for IELTS Writing.',
      questions: [
        {
          text: 'Which is the most formal replacement for "lots of people"?',
          options: ['lots of people', 'a bunch of people', 'a significant number of individuals', 'tons of people'],
          answer: 'a significant number of individuals',
          explanation: '"A significant number of individuals" is formal and precise. All other options are informal or colloquial.',
        },
        {
          text: 'Which is the most formal replacement for "find out"?',
          options: ['find out', 'figure out', 'ascertain', 'check out'],
          answer: 'ascertain',
          explanation: '"Ascertain" is the formal single-word equivalent of "find out." "Figure out" and "check out" are also informal phrasal verbs.',
        },
        {
          text: 'Which is the most formal replacement for "get better"?',
          options: ['get better', 'improve', 'pick up', 'get good'],
          answer: 'improve',
          explanation: '"Improve" is the formal single-word verb. "Get better" and "pick up" are informal phrasal constructions.',
        },
        {
          text: 'Which is the most formal replacement for "deal with the problem"?',
          options: ['deal with the problem', 'handle the problem', 'address the issue', 'sort out the problem'],
          answer: 'address the issue',
          explanation: '"Address the issue" is the most academic collocation. "Deal with" and "sort out" are phrasal verbs; "handle" is semi-formal.',
        },
      ],
    },
    {
      title: 'Replace Informal Words',
      type: 'fill-in',
      instructions: 'Rewrite the underlined informal word with an appropriate formal alternative.',
      questions: [
        {
          text: 'Original: "The government should HELP citizens access healthcare." Formal replacement for HELP:',
          answer: 'assist',
          explanation: '"Assist" is the formal equivalent of "help" in an academic context. "Facilitate" is also acceptable.',
        },
        {
          text: 'Original: "A LOT OF students prefer online learning." Formal replacement for A LOT OF:',
          answer: 'numerous',
          explanation: '"Numerous" is a formal quantifier. "A considerable number of" and "substantial" are also acceptable.',
        },
        {
          text: 'Original: "Pollution has BAD effects on health." Formal replacement for BAD:',
          answer: 'adverse',
          explanation: '"Adverse" is the formal adjective for negative effects. "Detrimental" and "unfavourable" also work.',
        },
        {
          text: 'Original: "The chart SHOWS a rise in unemployment." Formal replacement for SHOWS:',
          answer: 'illustrates',
          explanation: '"Illustrates" is formal and precise for data commentary. "Demonstrates" and "indicates" are also appropriate.',
        },
      ],
    },
    {
      title: 'Identify Register Problems',
      type: 'select',
      instructions: 'Identify the informality problem in each sentence.',
      questions: [
        {
          text: '"Education isn\'t just about getting good grades — it\'s about personal development too."',
          options: ['contractions (isn\'t, it\'s)', 'vocabulary is too complex', 'sentence is too long', 'No register problem'],
          answer: 'contractions (isn\'t, it\'s)',
          explanation: 'Contractions are never appropriate in IELTS Writing. Write "is not" and "it is" in full.',
        },
        {
          text: '"Scientists have looked into the effects of social media on kids."',
          options: ['passive voice', 'phrasal verb (looked into) and informal word (kids)', 'sentence too short', 'No register problem'],
          answer: 'phrasal verb (looked into) and informal word (kids)',
          explanation: '"Looked into" should be "investigated" or "examined." "Kids" should be "children" or "young people."',
        },
        {
          text: '"But is it really fair to make everyone pay higher taxes?"',
          options: ['starting with "But"', 'rhetorical question', 'word choice', 'Both: starting with "But" and rhetorical question'],
          answer: 'Both: starting with "But" and rhetorical question',
          explanation: 'Rhetorical questions and starting sentences with "But" are both informal. Rewrite: "The fairness of imposing higher taxes on all citizens is debatable."',
        },
        {
          text: '"I reckon that governments should do more to sort out pollution."',
          options: [
            '"reckon" (too informal) and "sort out" (phrasal verb)',
            'The sentence is too short',
            'Passive voice is needed',
            'No register problem',
          ],
          answer: '"reckon" (too informal) and "sort out" (phrasal verb)',
          explanation: '"Reckon" is colloquial — use "believe" or "argue." "Sort out" is a phrasal verb — use "address" or "tackle."',
        },
      ],
    },
    {
      title: 'Rewrite in Formal Register',
      type: 'writing',
      instructions: 'Rewrite each informal sentence in a formal academic register suitable for IELTS Writing Task 2.',
      questions: [
        {
          text: 'Rewrite: "Lots of kids these days spend too much time on their phones and it\'s really bad for them."',
          sampleAnswer: 'A considerable number of young people spend excessive amounts of time on mobile devices, which can be detrimental to their well-being.',
          explanation: 'The rewrite replaces "lots of" → "a considerable number of", "kids" → "young people", "it\'s" → removes contraction, "really bad" → "detrimental."',
        },
        {
          text: 'Rewrite: "I think the government should do something about the housing crisis because it\'s getting worse."',
          sampleAnswer: 'It is imperative that the government takes decisive action to address the housing crisis, which continues to intensify.',
          explanation: 'The rewrite replaces "I think" → "It is imperative that", "do something" → "takes decisive action", "it\'s getting worse" → "continues to intensify."',
        },
        {
          text: 'Rewrite: "People who don\'t have a college degree find it hard to get good jobs these days."',
          sampleAnswer: 'Individuals without higher education qualifications often encounter significant difficulties in securing well-paid employment in the current job market.',
          explanation: 'The rewrite replaces "don\'t" → removes contraction, "college degree" → "higher education qualifications", "find it hard" → "encounter difficulties", "get good jobs" → "securing well-paid employment."',
        },
      ],
    },
  ],
  tips: [
    'The fastest way to improve register: eliminate all contractions and replace 5 informal words',
    'Keep a two-column list in your notebook: informal word on the left, 2-3 formal alternatives on the right',
    'Read IELTS Band 9 model essays and notice the register — you will never see "kids", "lots of", or "stuff"',
    'In Speaking, slight informality is acceptable in Parts 1-2, but Part 3 should be semi-formal',
    'When in doubt, choose the longer, Latin-derived word: "obtain" vs "get", "demonstrate" vs "show"',
  ],
  dailyPractice: [
    'Take any paragraph from social media or a text message and rewrite it in formal academic register',
    'Review one of your past IELTS essays and highlight every informal word — then replace each one',
    'Write a Task 2 introduction using at least 3 formal vocabulary swaps from the table',
    'Practise formal sentence openings: write 5 sentences starting with "It is widely…", "There is growing…", etc.',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Formal Register for IELTS: Informal-to-formal vocabulary swaps (get→obtain, help→assist, lots of→numerous, bad→adverse), avoiding contractions and phrasal verbs, formal sentence structures (impersonal constructions, nominalisation). Difficulty 4.',
};

export function getDataForDifficulty(difficulty = 4) {
  if (difficulty <= 2) {
    return {
      ...formalRegisterData,
      title: formalRegisterData.title + ' (Foundations)',
      overview: { ...formalRegisterData.overview, duration: '15-20 minutes' },
      sections: formalRegisterData.sections.slice(0, Math.max(2, Math.ceil(formalRegisterData.sections.length / 2))),
      exercises: formalRegisterData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...formalRegisterData,
      title: formalRegisterData.title + ' (Advanced)',
      overview: { ...formalRegisterData.overview, duration: '30-35 minutes' },
      sections: formalRegisterData.sections,
      exercises: formalRegisterData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(formalRegisterData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return formalRegisterData;
}
